import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import NotificationCenter from "../common/NotificationCenter";
import GlobalSearch from "../common/GlobalSearch";

const HeaderContainer = styled.header`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  position: relative;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const MobileMenuButton = styled.button`
  display: none;
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  cursor: pointer;
  margin-right: 16px;
  border-radius: 8px;
  color: #64748b;

  &:hover {
    background: #f1f5f9;
    color: #1e293b;
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  align-items: center;
  margin-right: 32px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const BreadcrumbItem = styled.span`
  color: #64748b;
  font-size: 0.9rem;

  &.active {
    color: #1e293b;
    font-weight: 600;
  }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 8px;
  color: #cbd5e1;
`;

const SearchContainer = styled.div`
  flex: 1;
  max-width: 400px;
  margin-right: 24px;

  @media (max-width: 768px) {
    max-width: 150px;
    margin-right: 12px;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

// Removed old notification button styles - now using NotificationCenter component

const UserProfileContainer = styled.div`
  position: relative;
`;

const UserProfileButton = styled.button`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background: #f1f5f9;
  }
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  margin-right: 8px;
`;

const UserInfo = styled.div`
  text-align: left;

  @media (max-width: 768px) {
    display: block;
  }
`;

const UserName = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #1e293b;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const UserRole = styled.div`
  font-size: 0.75rem;
  color: #64748b;

  @media (max-width: 768px) {
    display: none; /* Hide role on mobile to save space */
  }
`;

const DropdownArrow = styled.span`
  margin-left: 8px;
  color: #64748b;
  font-size: 0.8rem;
`;

const UserDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: 4px;
  min-width: 200px;
  z-index: 50;
  display: ${(props) => (props.$show ? "block" : "none")};
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  font-size: 0.9rem;
  color: #475569;

  &:hover {
    background: #f8fafc;
  }

  &.danger {
    color: #ef4444;
  }
`;

const DashboardHeader = ({ onToggleMobileMenu }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  const userDropdownRef = useRef(null);

  // Generate user initials from name
  const getUserInitials = (firstName, lastName) => {
    if (!firstName && !lastName) return 'U';
    const fullName = `${firstName || ''} ${lastName || ''}`.trim();
    if (!fullName) return 'U';
    return fullName
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Combine firstName and lastName if they exist, otherwise use name or email
  const getUserFullName = () => {
    if (user?.firstName || user?.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }
    return user?.name || user?.email || 'Usuário';
  };

  const displayUser = {
    name: getUserFullName(),
    role: user?.role || user?.primaryRole || 'Usuário',
    initials: getUserInitials(user?.firstName, user?.lastName) || 'U'
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target)
      ) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Search functionality is now handled by GlobalSearch component

  const handleNotificationNavigation = (url) => {
    navigate(url);
  };

  const handleLogout = async () => {
    try {
      const result = logout();
      if (result.success) {
        setShowUserDropdown(false);
        navigate('/login', { replace: true });
      } else {
        console.error('Logout failed:', result.error);
        // Still navigate to login even if logout had issues
        navigate('/login', { replace: true });
      }
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login', { replace: true });
    }
  };

  const getBreadcrumbs = () => {
    const path = window.location.pathname;
    const segments = path.split("/").filter(Boolean);

    const breadcrumbMap = {
      dashboard: "Dashboard",
      assessments: "Avaliações",
      okrs: "OKRs",
      pdis: "PDIs",
      feedbacks: "Feedbacks",
      meetings: "Reuniões",
      surveys: "Pesquisas",
      reports: "Relatórios",
      admin: "Administração",
    };

    return segments.map((segment) => breadcrumbMap[segment] || segment);
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <HeaderContainer>
      <LeftSection>
        <MobileMenuButton onClick={onToggleMobileMenu}>☰</MobileMenuButton>

        <BreadcrumbContainer>
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={index}>
              <BreadcrumbItem
                className={index === breadcrumbs.length - 1 ? "active" : ""}
              >
                {crumb}
              </BreadcrumbItem>
              {index < breadcrumbs.length - 1 && (
                <BreadcrumbSeparator>/</BreadcrumbSeparator>
              )}
            </React.Fragment>
          ))}
        </BreadcrumbContainer>

        <SearchContainer>
          <GlobalSearch placeholder="Buscar em toda a plataforma..." />
        </SearchContainer>
      </LeftSection>

      <RightSection>
        <NotificationCenter
          onNotificationClick={handleNotificationNavigation}
        />

        <UserProfileContainer ref={userDropdownRef}>
          <UserProfileButton
            onClick={() => setShowUserDropdown(!showUserDropdown)}
          >
            <UserAvatar>{displayUser.initials}</UserAvatar>
            <UserInfo>
              <UserName>{displayUser.name}</UserName>
              <UserRole>{displayUser.role}</UserRole>
            </UserInfo>
            <DropdownArrow>▼</DropdownArrow>
          </UserProfileButton>

          <UserDropdown $show={showUserDropdown}>
            <DropdownItem onClick={() => navigate("/profile")}>
              👤 Meu Perfil
            </DropdownItem>
            <DropdownItem onClick={() => navigate("/settings")}>
              ⚙️ Configurações
            </DropdownItem>
            <DropdownItem onClick={() => navigate("/help")}>
              ❓ Ajuda
            </DropdownItem>
            <DropdownItem className="danger" onClick={handleLogout}>
              🚪 Sair
            </DropdownItem>
          </UserDropdown>
        </UserProfileContainer>
      </RightSection>
    </HeaderContainer>
  );
};

export default DashboardHeader;
